/**
 * The face contract.
 *
 * Everything an app needs to show a voice assistant's face lives in this file.
 * The app owns the audio and the conversation; the face owns the pixels. Neither
 * knows anything about the other beyond the four verbs on {@link Face}.
 */

/** Coarse conversational state. Every face must render all four distinguishably. */
export type FaceMode = 'idle' | 'listening' | 'thinking' | 'speaking';

/**
 * Optional expressive hint, e.g. `'happy'`. Deliberately a free string: faces map
 * what they understand and ignore the rest, so an app can pass through whatever
 * its model emits without a schema negotiation.
 */
export type FaceEmotion = 'neutral' | 'happy' | 'sad' | 'angry' | 'surprised';

/** Well-known emotions. A face is only obliged to handle `'neutral'`. */
export const FACE_EMOTIONS: readonly FaceEmotion[] = [
  'neutral',
  'happy',
  'sad',
  'angry',
  'surprised',
];

export interface FaceState {
  mode: FaceMode;
  emotion?: FaceEmotion | (string & {});
}

/**
 * The five vowel mouth shapes VRM standardises on. Chosen because they are the
 * common denominator: VRM rigs expose exactly these, and simpler faces can blend
 * them into a single mouth outline.
 */
export type VisemeId = 'aa' | 'ih' | 'ou' | 'ee' | 'oh';

export const VISEME_IDS: readonly VisemeId[] = ['aa', 'ih', 'ou', 'ee', 'oh'];

export interface Viseme {
  id: VisemeId;
  /** 0..1. Across a frame these sum to roughly `MouthFrame.level`. */
  weight: number;
}

/**
 * One ~60fps frame of drive data derived from the audio being played.
 *
 * `visemes` is optional on purpose. A source that only knows loudness (a level
 * meter from a server, say) omits it, and every face must still work — degrading
 * to a level-driven open/close mouth.
 */
export interface MouthFrame {
  /** Seconds, monotonic. Only differences are meaningful. */
  t: number;
  /** Mouth openness, 0 (closed) .. 1 (wide). Already smoothed. */
  level: number;
  visemes?: Viseme[];
  /**
   * The lips are shut for a stop (m, b, p…): the voice just dropped far below its smoothed
   * level. `level` cannot show it, since its release outlasts a 20–60 ms closure.
   */
  closure?: boolean;
}

/** Options every face understands. Individual faces extend this. */
export interface FaceOptions {
  /** Cap on device pixel ratio. Defaults to `min(devicePixelRatio, 2)`. */
  pixelRatio?: number;
  /** CSS colour painted behind the face. */
  background?: string;
  /** CSS colour driving glow and highlights. */
  accent?: string;
}

/**
 * A mounted, self-animating face.
 *
 * Lifecycle: `mount` once, then `setState`/`drive`/`resize` as often as you like,
 * then `dispose`. The face runs its own animation loop — `drive` only supplies
 * data, it does not render. Calls after `dispose` are no-ops, not errors.
 */
export interface Face {
  readonly id: string;
  /** Takes over `container`: appends its canvas and starts animating. */
  mount(container: HTMLElement): Promise<void>;
  setState(state: FaceState): void;
  /** Called at audio frame rate while the assistant speaks. */
  drive(frame: MouthFrame): void;
  /** CSS pixels. */
  resize(w: number, h: number): void;
  dispose(): void;
}

/**
 * A face that takes a user-supplied asset at runtime — the VRM avatar needs a
 * `.vrm`, which licensing forbids us from bundling. Lets an app offer a file
 * picker without knowing, or caring, which face is currently mounted.
 */
export interface ModelFace extends Face {
  loadModel(source: string | Blob): Promise<void>;
  readonly hasModel: boolean;
}

export function acceptsModel(face: Face): face is ModelFace {
  return typeof (face as ModelFace).loadModel === 'function';
}

/** A face module's entry point. One per file under `src/faces/`. */
export type FaceFactory<O extends FaceOptions = FaceOptions> = (opts?: O) => Face;
